import {Storage} from "@/services/Storage";
import axios from "axios";
import router from "@/router";
import {generateUUID} from "@/utils/uuid";

export default {
    name: 'kazang',
    state: {
        kazangSessionID: null,
        kazangProductList: [],
    },
    getters: {
        getKazangProductList: (state) => state.kazangProductList,
        getKazangSessionID: (state) => state.kazangSessionID,
        getKazangProviders: (state) => state.kazangProductList.map(item => item.Provider),
        getKazangProductListByProvider: (state) => (provider) => {
            return state.kazangProductList
                .find(item => {
                    return item.Provider === provider
                })
        }
    },
    mutations: {
        setKazangTimeStampProperty: (state, payload) => state.kazangTimeStamp = payload,
        setKazangSession: async (state, data) => {
            if (!data) {
                data = await Storage.getKazangSession();
            }
            state.kazangSessionID = data
        },

        clearKazangSession: async (state) => {
            await Storage.setKazangSession(null);
            state.kazangSessionID = null;
        },

        setKazangProductList: async (state, data) => {
            if (!data) {
                data = await Storage.getKazangProductList();
            }
            state.kazangProductList = data
        },
    },
    actions: {
        kazangLogin: async (context) => {
            let store = await Storage.getDefaultStore();
            return axios.post(kazangApi + `login/${store.StoreDB}`)
                .then(({data}) => context.commit(`setKazangSession`, data.sessionId))
                .catch(() => context.commit(`setHasKazang`, false));
        },
        kazangProductList: async (context) => {
            let store = await Storage.getDefaultStore();
            return axios.get(kazangApi + `products/${store.StoreDB}`)
                .then(({data}) => context.commit(`setKazangProductList`, data.productList))
                .catch((err) => errorHandler.tomcatError(err))
        },

        // Initial step for handling Kazang payments (new unified flow)
        firstKazangPaymentStep: async ({dispatch, getters}, payload) => {
            let store = await Storage.getDefaultStore();
            let settings = await Storage.getDeviceSettings();
            let saleDetails = getters.getSaleDetails;
            let amount = Number(saleDetails.total) - Number(getters.getSaleDiscount.total);

            // 1) Validate stock first (kept from previous flow)
            const valid = {
                PC: settings.PC,
                items: getters.getSaleLines
            };
            try {
                const {data: stockCheck} = await axios.post(serverApi + `invoice/${store.StoreDB}/checkStockAvailability`, valid);
                if (!stockCheck.valid) throw new Error(stockCheck.errorMessage || 'Stock validation failed');
            } catch (err) {
                return errorHandler.tomcatError(err);
            }

            // 2) Prepare request payload for new unified Kazang endpoint
            const msisdn = typeof payload === 'object' ? payload.customerContact : payload;
            const splitAmount = typeof payload === 'object' && payload.splitArray && payload.splitArray.Kazang != null
                ? Number(payload.splitArray.Kazang.toFixed(2))
                : Number(amount.toFixed(2));
            const network = await dispatch('checkCustomerNetwork', msisdn);

            // Generate client idempotency key (LocalID)
            const localid = `kaz-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;

            const pc = encodeURIComponent(settings.PC || 'Online');
            const url = `${serverApi}api/payments/kazang/${store.StoreDB}?pc=${pc}`;

            try {
                const {data} = await axios.post(url, {
                    localid,
                    amount: splitAmount,
                    msisdn,
                    network
                });

                try {
                    // Convert string to Base64
                    const base64Receipt = btoa(unescape(encodeURIComponent(data.response.print)));
                    await dispatch('sendPrintToLocal', base64Receipt);

                } catch (error) {
                    console.error('Error encoding receipt:', error.message);
                }
                // Use LocalID from server echo as invoice reference
                const reference = data && (data.LocalID || data.localid) ? (data.LocalID || data.localid) : localid;

                return dispatch('processKazangInvoice', {
                    amount: splitAmount,
                    splitObj: typeof payload === 'object' ? {...payload, SecondRefrence: reference} : null,
                    SecondRefrence: reference
                });
            } catch (err) {
                return errorHandler.tomcatError(err, true);
            }
        },

        // Sends a payment request with payload details
        sendPayment: async ({dispatch, getters}, {payload, store, amount}) => {
            let settings = await Storage.getDeviceSettings();
            const msisdn = payload.customerContact;
            const splitAmount = Number(payload.splitArray.Kazang.toFixed(2));
            const network = await dispatch('checkCustomerNetwork', msisdn);
            const localid = `kaz-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
            const pc = encodeURIComponent(settings.PC || 'Online');
            const url = `${serverApi}api/payments/kazang/${store.StoreDB}?pc=${pc}`;

            try {
                const {data} = await axios.post(url, {
                    localid,
                    amount: splitAmount,
                    msisdn,
                    network
                });
                // Handle response similar to firstKazangPaymentStep
                const reference = data && (data.LocalID || data.localid) ? (data.LocalID || data.localid) : localid;
                return dispatch('handlePaymentResponse', {
                    data: { SecondRefrence: reference, Response: '0' },
                    amount,
                    payload
                });
            } catch (err) {
                return dispatch('handlePaymentError', err);
            }
        },

        // Sends a simpler payment request when payload is not an object
        sendSimplePayment: async ({dispatch, getters}, {payload, store, amount}) => {
            let settings = await Storage.getDeviceSettings();
            const msisdn = typeof payload === 'object' ? payload.customerContact : payload;
            const splitAmount = Number(amount.toFixed(2));
            const network = await dispatch('checkCustomerNetwork', msisdn);
            const localid = `kaz-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
            const pc = encodeURIComponent(settings.PC || 'Online');
            const url = `${serverApi}api/payments/kazang/${store.StoreDB}?pc=${pc}`;

            try {
                const {data} = await axios.post(url, {
                    localid,
                    amount: splitAmount,
                    msisdn,
                    network
                });
                const reference = data && (data.LocalID || data.localid) ? (data.LocalID || data.localid) : localid;
                return dispatch('handlePaymentResponse', {
                    data: { SecondRefrence: reference, Response: '0', customerContact: msisdn },
                    amount,
                    payload
                });
            } catch (err) {
                return dispatch('handlePaymentError', err);
            }
        },

        // Handles the response from payment API calls
        handlePaymentResponse: ({dispatch}, {data, amount, payload = null}) => {
            if (!data.secondStep && data.Response === '0') {
                return dispatch('processKazangInvoice', {
                    amount,
                    splitObj: payload,
                    SecondRefrence: data.SecondRefrence
                });
            } else if (data.Response === '3' || data.Response === '41') {
                return dispatch('resetKazangUser', data.Response);
            } else if (data.Response !== '0') {
                throw new Error(data.Print);
            }
            return dispatch('retryKazangPaymentStep', {data, payload});
        },

        // Manages retry mechanism for payment steps
        retryKazangPaymentStep: ({dispatch}, {data, payload}) => {
            let attemptCount = 0;
            const maxAttempts = 35;
            let lastError = null;

            return new Promise((resolve, reject) => {
                const attemptKazangStep = () => {
                    attemptCount++;

                    if (attemptCount > maxAttempts) {
                        console.error('Max attempts reached. Stopping retries.');
                        reject(lastError || 'Max attempts reached without specific error message.');
                        return;
                    }
                    return dispatch('secondKazangStep', {data, payload})
                        .then(response => {
                            if (response.success) {

                                resolve(response);  // Resolve on successful response
                                return;
                            } else {
                                // Log and prepare for another retry
                                lastError = response.message || 'Non-successful response.';
                                setTimeout(attemptKazangStep, 3000);  // Retry after a delay
                            }
                        })
                        .catch(error => {
                            // Even on errors, prepare for another retry unless max attempts are reached
                            lastError = error.toString();
                            console.error(`Dispatch error on attempt ${attemptCount}: ${lastError}`);
                            setTimeout(attemptKazangStep, 3000);
                        });
                };

                attemptKazangStep();  // Initiate the first attempt
            });
        },

        // Handles any payment errors
        handlePaymentError: ({commit}, err) => {
            commit('clearKazangSession');
            errorHandler.tomcatError(err, true);
            return Promise.reject(err); // Ensuring errors are propagated back if necessary
        },

        resetKazangUser: (context, responseCode) => {
            return Swal.fire({
                title: 'Account Update',
                html: `<input type="number" id="login" style="width: 70%" class="swal2-input" placeholder="Kazang Username">
                       <input type="password" id="password" style="width: 70%" class="swal2-input" placeholder="Kazang Password">`,
                confirmButtonText: 'Sign in',
                focusConfirm: false,
                preConfirm: async () => {
                    const login = Swal.getPopup().querySelector('#login').value
                    const password = Swal.getPopup().querySelector('#password').value
                    if (!login || !password) {
                        return Swal.showValidationMessage(`Please enter login and password`)
                    }
                    let obj = {
                        username: login,
                        password: password
                    };
                    let store = await Storage.getDefaultStore();
                    return axios.post(franchiseTomcatApi + `SetKazangUser/${store.StoreDB}`, obj)
                        .then(async () => await Storage.setAdminUser(obj))
                        .catch(err => Swal.showValidationMessage(errorHandler.tomcatError(err, true)))
                }
            }).then(res => {
                if (res.isConfirmed) {
                    let message = responseCode === `3` ? `Account Updated , Please try again` :
                        `Account Updated , Try again in 30 min`;
                    return Swal.fire(
                        `Success!`,
                        message,
                        `success`
                    ).finally(() => {
                        throw new Error(message)
                    });
                }
            });
        },

        /**
         * Initiates the first leg of a Spenn split-payment against the Kazang Spenn endpoint.
         *
         * Two payload shapes are accepted (kept for backwards compatibility with non-split callers):
         *  - object: split-payment payload carrying `splitArray` map and `customerContact`
         *  - string: customer cell number for a full (non-split) Spenn charge
         *
         * Bug I1 (fixed 2026-07-31): the object branch previously read
         *     payload.splitArray.Kazang.toFixed(2)
         * as the amount, which is a copy-paste from firstKazangPaymentStep. The Spenn amount must
         * come from `splitArray.Spenn`. Without this fix, every Spenn split-payment would post
         * the Kazang split amount (or NaN if no Kazang split was configured).
         *
         * @param {object} context Vuex action context
         * @param {object|string} payload split-payment payload (object) or customer cell (string)
         * @returns {Promise<object>} Spenn first-step response (or dispatched downstream promise)
         */
        firstSpennPaymentStep: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            let saleDetails = context.getters.getSaleDetails;
            let amount = Number(saleDetails.total) - Number(context.getters.getSaleDiscount.total);

            if (typeof payload === 'object') {
                return axios.post(kazangApi + `pay/${store.StoreDB}/spennPayment`, {
                    session: context.getters.getKazangSessionID,
                    amount: Number(payload.splitArray.Spenn.toFixed(2)),
                    cell: payload.customerContact
                }).then(({data}) => {
                    if (!data.secondStep && data.Response === '0') {
                        return context.dispatch('processKazangInvoice', {
                            amount: amount,
                            splitObj: payload,
                            SecondRefrence: data.SecondRefrence
                        })
                    } else if (data.Response === '3') {
                        return context.dispatch(`resetKazangUser`, data.Response);
                    } else if (data.Response === '41') {
                        return context.dispatch(`resetKazangUser`, data.Response);
                    } else if (data.Response !== '0') {
                        throw new Error(data.Print)
                    } else {
                        data.customerContact = payload.customerContact;
                        data.splitObj = payload;
                        context.commit('setKazangSession', data.newSession)
                        return data;
                    }
                }).catch((err) => {
                    context.commit('clearKazangSession')
                    errorHandler.tomcatError(err)
                })
            }

            return axios.post(kazangApi + `pay/${store.StoreDB}/spennPayment`, {
                session: context.getters.getKazangSessionID,
                amount: Number(amount.toFixed(2)),
                cell: payload
            }).then(({data}) => {
                if (!data.secondStep && data.Response === '0') {
                    return context.dispatch('processKazangInvoice', {
                        amount: amount,
                        splitObj: null,
                        SecondRefrence: data.SecondRefrence
                    })
                } else if (data.Response === '3') {
                    return context.dispatch(`resetKazangUser`, data.Response);
                } else if (data.Response === '41') {
                    return context.dispatch(`resetKazangUser`, data.Response);
                } else if (data.Response !== '0') {
                    throw new Error(data.Print)
                } else {
                    data.customerContact = payload;
                    context.commit('setKazangSession', data.newSession)
                    return data;
                }
            }).catch((err) => {
                context.commit('clearKazangSession')
                errorHandler.tomcatError(err, true)
            })
        },

        secondSpennStep: async (context, firstStepResponse) => {
            let store = await Storage.getDefaultStore();
            return axios.post(kazangApi + `pay/${store.StoreDB}/spennApprove`, JSON.parse(firstStepResponse.SecondStepPayload))
                .then(async ({data}) => {
                    if (!data.Error) {
                        if (!data.approved) return {Error: 'Not Confirmed'};
                        return context.dispatch('processKazangInvoice', {
                            amount: firstStepResponse.amount,
                            splitObj: firstStepResponse.splitObj ? firstStepResponse.splitObj : null,
                            SecondRefrence: data.SecondRefrence
                        })
                    }
                    return data
                }).catch((err) => {
                    context.commit('clearKazangSession')
                    errorHandler.tomcatError(err, true)
                })
        },


        secondKazangStep: async (context, firstStepResponse) => {
            let splitObj = firstStepResponse.payload;
            firstStepResponse = firstStepResponse.data
            let contact = firstStepResponse.customerContact;
            if (!contact) {
                if (firstStepResponse.cell_ID) {
                    contact = `0` + firstStepResponse.cell_ID;
                }
            }
            let store = await Storage.getDefaultStore();
            let network = await context.dispatch('checkCustomerNetwork', contact);
            let route = ''

            switch (network) {
                case 'MTN':
                    route = '/mtnApprove';
                    break
                case 'Airtel':
                    route = '/airtelApprove';
                    break
                default:
                    route = '/zamtelApprove'
                    break
            }
            let url = kazangApi + `pay/${store.StoreDB}${route}`;
            return axios.post(url, JSON.parse(firstStepResponse.SecondStepPayload))
                .then(async ({data}) => {
                    if (!data.Error) {
                        if (!data.approved) {
                            return {
                                Error: 'Not Confirmed'
                            }
                        }
                        return context.dispatch('processKazangInvoice', {
                            amount: firstStepResponse.amount,
                            splitObj: splitObj ? splitObj : null,
                            SecondRefrence: data.SecondRefrence
                        })
                    }
                    if (!data.approved) {
                        const printMsg = data.print || data.Print;
                        if (printMsg) {
                            return {
                                Error: printMsg
                            }
                        }
                    }
                    return data
                }).catch((err) => {
                    context.commit('clearKazangSession')
                    errorHandler.tomcatError(err)
                })

        },

        processKazangInvoice: (context, response) => {
            if (response.splitObj) {
                response.splitObj.SecondRefrence = response.SecondRefrence;
                return context.dispatch('processTransaction', response.splitObj)
                    .then(() => {
                        return {success: true}
                    })
            }
            return context.dispatch('processTransaction', {
                refund: false,
                total: Number(response.amount.toFixed(2)),
                paid: Number(response.amount.toFixed(2)),
                SecondRefrence: response.SecondRefrence,
                change: 0,
                type: "Kazang"
            }).then(() => {
                return {success: true}
            })
        },
        checkCustomerNetwork(context, phoneNumber) {
            let prefix = phoneNumber.slice(0, 3);
            switch (prefix) {
                case '096':
                case '076':
                    return 'MTN';
                case '097':
                case '077':
                    return 'Airtel'
                default :
                    return 'Zamtel';
            }
        },

        requestPasswordReset: async (context, username) => {
            return axios.post(franchiseTomcatApi + `SetKazangUser/${username}/forgotPasswordRequest`, {
                username: username
            }).catch(err => errorHandler.tomcatError(err))
        },

        resetPassword: async (context, payload) => {
            return axios.post(franchiseTomcatApi + `SetKazangUser/${payload.username}/resetPassword`, payload)
                .then(() => router.push({name: 'login'}))
                .catch(err => errorHandler.tomcatError(err))
        },

        // Get pending Kazang transactions from the backend
        getPendingKazangTransactions: async (context) => {
            let store = await Storage.getDefaultStore();

            // Get Kazang API key from device settings
            let settings = await Storage.getDeviceSettings();
            let apiKey = settings.kazangApiKey || settings.KazangApiKey || '';

            console.log('[Kazang] Store:', store.StoreDB);
            console.log('[Kazang] Settings:', settings);
            console.log('[Kazang] API Key from device settings:', apiKey ? 'Found' : 'Not found');

            // If not in device settings, try to get from backend
            if (!apiKey) {
                console.log('[Kazang] Fetching API key from backend...');
                try {
                    const {data} = await axios.get(serverApi + `api/payments/kazang/${store.StoreDB}/apikey`);
                    apiKey = data?.kazangSmartApiKey || '';
                    console.log('[Kazang] API Key from backend:', apiKey ? 'Found' : 'Not found');
                } catch (err) {
                    // Use backend error message if available
                    const backendMessage = err.response?.data?.message || err.response?.data?.error;
                    if (backendMessage) {
                        throw new Error(backendMessage);
                    }
                    console.error('[Kazang] Failed to get API key from backend:', err);
                }
            }

            if (!apiKey) {
                throw new Error('Kazang API key not configured. Please contact support.');
            }

            const url = kazangSubscriptionApi + `V2/pay/${store.StoreDB}/pending/${apiKey}`;
            console.log('[Kazang] Calling API:', url);

            // Call Kazang API directly using the API key from device settings
            // Note: API key is passed in URL path AND as header (as required by Kazang API)
            return axios.get(url, {
                headers: {
                    'Api-Key': apiKey,
                    'Content-Type': 'application/json'
                }
            })
                .then(({data}) => {
                    // Map API response to expected format
                    // Response has: amount, phone, reference, apiKey, status
                    return data.map(item => ({
                        localID: item.reference || item.apiKey, // Use reference as ID, fallback to apiKey
                        phone: item.phone || '',
                        amount: parseFloat(item.amount) || 0,
                        network: item.status || 'PENDING', // Use status as network display
                        status: item.status || 'PENDING',
                        date: new Date().toISOString() // Current timestamp (not provided by API)
                    }));
                })
                .catch(err => {
                    errorHandler.tomcatError(err);
                    throw err;
                });
        },

        // Process a pending Kazang transaction
        processPendingKazangTransaction: async (context, localId) => {
            let store = await Storage.getDefaultStore();
            // Generate a new localID for the process request
            const generatedLocalId = generateUUID();

            // Get Kazang API key from device settings
            let settings = await Storage.getDeviceSettings();
            let apiKey = settings.kazangApiKey || settings.KazangApiKey || '';

            // If not in device settings, try to get from backend
            if (!apiKey) {
                try {
                    const {data} = await axios.get(serverApi + `api/payments/kazang/${store.StoreDB}/apikey`);
                    apiKey = data?.kazangSmartApiKey || '';
                } catch (err) {
                    // Use backend error message if available
                    errorHandler.tomcatError(err)
                    console.error('Failed to get Kazang API key from backend:', err);
                }
            }

            if (!apiKey) {
                throw new Error('Kazang API key not configured. Please contact support.');
            }

            // return axios.post(kazangSubscriptionApi + `V2/pay/${store.StoreDB}/pending/${generatedLocalId}/process`, {}, {
            return axios.post(kazangSubscriptionApi + `V2/pay/kazanglogs/pending/${apiKey}/process`, {}, {
                headers: {
                    'Api-Key': apiKey,
                    'Content-Type': 'application/json'
                }
            })
                .then(({data}) => {
                    return data;
                })
                .catch(err => {
                    errorHandler.tomcatError(err);
                    throw err;
                });
        },

    }
}
